// D-1026: SGP の発注で「要確認」に出す行の判定(純関数。何も書かない・何も読まない)。
//
// ■ 元ネタ(ひろ 2026-10-09)
//   「SGPは納期遅れが出たら修正と確認を出すようにしよう」「RZのトップのタナサポに要確認バッジ出して」。
//   実例: 誕生石コレクション(order 1e57cd31…)は SGP メモが「10/8発送完了 9/28 納期遅延連絡済み …」なのに
//   RZ では 9/26 に荷受け済みになっていた(実物は 10/9 着)。
//
// ■ 判定の種類
//   A = 届く前の荷受けの疑い(status='arrived'):
//       ・SGP メモの発送日(「M/D発送完了」「M/D出荷」「商品発送M/D」など)が、荷受け日(arrived_at の JST 日付)より後。
//       ・または、メモに発送の記録が無く、受付・遅延の状態のまま(発注受付 / 受注完了 / 受付完了 / 確認中 / 手配済 /
//         納期遅延 / 伝票変更 / 出荷停止 / 納品延期)。発送予定日(「M/D発送予定」)が荷受け日より後のときも A。
//       ・「￥ウ」「小￥」のように状態の言葉が無いメモ・空のメモは判定できないので A にしない。
//   B = 納期変更(status が ordered / shipped で、まだ受け取り切っていない):
//       メモに「納期遅延」「伝票変更」「納期変更」「納品延期」「出荷停止」がある。荷受けリストに納期変更後の文言を出す。
//
// ■ メモの読み方(本番 prize_orders.notes の実例から。テストは実例の文字列そのもの)
//   ・発送日 = 日付の **直後** に 発送完了 / 出荷完了 / 発送済(み) / 出荷済(み) / 出荷 / 発送 が付くもの、
//     または「商品発送M/D」。「予定」「先(発送先変更)」「停止」が続くものは発送ではない。
//   ・「M/D着発送完了」「M/D着にて発送完了」の日付は着日。発送はしている(日付は不明)扱い。
//   ・「納期変更前：…」「納期変更後：…」の中の日付は予定・過去の約束なので発送日として読まない
//     (例「納期変更後：8/10発送予定」「納期変更後：最短で7月1日出荷」)。
//   ・日付の年は order_date から決める(order_date の年。その年だと order_date より 31 日以上前になるなら翌年)。
//     12/20 発注の「1/10発送完了」は翌年 1/10。
//   ・発送日が複数あるとき(分納「4/22出荷 4/28出荷」)は **一番早い日** で A を判定する(最初の箱が届いた日より後か)。
//   ・全角数字・全角スラッシュ・全角コロンは半角にしてから読む(「８/４発送完了」)。
//   ・日付は 'YYYY-MM-DD' の文字列で比べる(Date の UTC ずれを持ち込まない)。

export const SGP_REVIEW_KIND = { A: 'A', B: 'B' }

// A の対象にする荷受けの始まり。RZ で荷受けした(stock_movements に in_arrival がある)行は
//   2026-07 以降だけで、それより前の arrived は過去データの取込(arrived_at が空か、移動の記録が無い)。
//   本番 2026-10-09 の読み取り: 2026-07 以降の SGP arrived 205 件は全件 in_arrival あり、2026-03 以前は 0 件。
export const SGP_REVIEW_ARRIVED_FROM = '2026-07-01'

const STATE_WORD_RE = /発注受付|受注完了|受付完了|確認中|手配済|手配|納期遅延|伝票変更|出荷停止|納品延期/
const DELAY_WORD_RE = /納期遅延|伝票変更|納期変更|納品延期|出荷停止/
const AUTO_CLEARED_RE = /\[auto-cleared[^\]]*\]/g
// 「納期変更前：…」「納期変更後：…」(次の空白まで)。中の日付は発送日として読まない。
const CHANGE_SEGMENT_RE = /納期変更[前後]\s*[:：][^\s]*/g
const DATED_SHIP_RE = /(\d{1,2})\s*(?:\/|月)\s*(\d{1,2})\s*日?\s*(?:発送完了|出荷完了|発送済み?|出荷済み?|出荷(?!予定|停止)|発送(?!予定|先|停止))/g
const SHOUHIN_HASSOU_RE = /商品発送\s*(\d{1,2})\s*\/\s*(\d{1,2})/g
const PLANNED_SHIP_RE = /(\d{1,2})\s*(?:\/|月)\s*(\d{1,2})\s*日?\s*(?:発送予定|出荷予定)/g
// 日付の付かない発送の記録(「発送完了」だけ、「1/27着発送完了」の着日型など)。
const UNDATED_SHIP_RE = /発送完了|出荷完了|発送済|出荷済/

export function normalizeSgpNotes(notes) {
  if (notes == null) return ''
  return String(notes)
    .replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0))
    .replace(/／/g, '/')
    .replace(/：/g, ':')
    .replace(AUTO_CLEARED_RE, ' ')
}

function pad(n) { return String(n).padStart(2, '0') }

function addDays(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d) + days * 86400000)
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`
}

// メモの「M/D」に年を付ける。orderDate が無いときは null(年が決められないので比べない)。
export function resolveSgpDate(month, day, orderDate) {
  const m = Number(month)
  const d = Number(day)
  if (!orderDate || !(m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null
  const orderYear = Number(String(orderDate).slice(0, 4))
  const floor = addDays(String(orderDate).slice(0, 10), -31)
  const same = `${orderYear}-${pad(m)}-${pad(d)}`
  return same < floor ? `${orderYear + 1}-${pad(m)}-${pad(d)}` : same
}

function collectDates(text, re, orderDate) {
  const out = []
  for (const m of text.matchAll(re)) {
    const ymd = resolveSgpDate(m[1], m[2], orderDate)
    if (ymd) out.push(ymd)
  }
  return out
}

// 「納期変更後：…」の文言(全部。最後が最新)。【納期遅延】 9月中～末→10中～下 の形も拾う。
function collectChangedTo(text) {
  const out = []
  for (const m of text.matchAll(/納期変更後\s*:\s*([^\s]*)/g)) {
    const v = m[1].trim()
    if (v) out.push(v)
  }
  if (out.length === 0) {
    const bracket = text.match(/【納期遅延】\s*([^\s]+)/)
    if (bracket) out.push(bracket[1])
    const stop = text.match(/([^\s]*(?:納品延期|出荷停止)[^\s]*)/)
    if (stop) out.push(stop[1].replace(/^⇒/, ''))
  }
  return out
}

// メモを読んで分かることを全部返す。
export function parseSgpNotes(notes, orderDate) {
  const text = normalizeSgpNotes(notes)
  const outside = text.replace(CHANGE_SEGMENT_RE, ' ')
  const shipDates = [
    ...collectDates(outside, DATED_SHIP_RE, orderDate),
    ...collectDates(outside, SHOUHIN_HASSOU_RE, orderDate),
  ].sort()
  const plannedShipDates = collectDates(outside, PLANNED_SHIP_RE, orderDate).sort()
  const hasUndatedShip = UNDATED_SHIP_RE.test(outside) || /商品発送/.test(outside)
  return {
    shipDates,
    hasShipRecord: shipDates.length > 0 || hasUndatedShip,
    plannedShipDates,
    hasStateWord: STATE_WORD_RE.test(text),
    hasDelay: DELAY_WORD_RE.test(text),
    changedTo: collectChangedTo(text),
  }
}

export function isSgpApiOrder(order) {
  return !!order && order.supplier_id === 'SGP' && order.order_source === 'sgp_api'
}

export function arrivedDateJst(arrivedAt) {
  if (!arrivedAt) return null
  const t = new Date(arrivedAt)
  if (Number.isNaN(t.getTime())) return null
  return t.toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
}

function md(ymd) {
  const [, m, d] = ymd.split('-').map(Number)
  return `${m}/${d}`
}

// B の「確認済み」を覚えておく鍵。納期変更後の文言が増えた・変わったら別の鍵 = もう一度出す。
export function delayKeyOf(parsed) {
  return parsed.changedTo.length ? parsed.changedTo.join(' / ') : '(納期変更の記載あり)'
}

// 1 行を判定する。要確認でなければ null。
//   戻り値: { kind: 'A'|'B', reason, summary: [string], key }
//   key = 確認済みと突き合わせる鍵(A は 'A'、B は納期変更後の文言)。
export function judgeSgpReview(order) {
  if (!isSgpApiOrder(order)) return null
  const notes = order.notes ?? ''
  if (/\[auto-cleared/.test(notes)) return null
  const parsed = parseSgpNotes(notes, order.order_date)

  if (order.status === 'arrived') {
    const arrived = arrivedDateJst(order.arrived_at)
    if (!arrived || arrived < SGP_REVIEW_ARRIVED_FROM) return null
    if (parsed.shipDates.length) {
      const firstShip = parsed.shipDates[0]
      if (firstShip <= arrived) return null
      const summary = [`SGP では ${md(firstShip)} 発送`]
      if (parsed.changedTo.length) summary.push(`納期変更後:${parsed.changedTo.at(-1)}`)
      return { kind: 'A', reason: 'ship_after_arrival', summary, key: 'A', parsed }
    }
    if (parsed.hasShipRecord) return null
    const lastPlan = parsed.plannedShipDates.at(-1)
    const planAfter = !!lastPlan && lastPlan > arrived
    if (!parsed.hasStateWord && !planAfter) return null
    const summary = ['SGP メモに発送の記録なし']
    if (planAfter) summary.push(`SGP の発送予定 ${md(lastPlan)}`)
    if (parsed.changedTo.length) summary.push(`納期変更後:${parsed.changedTo.at(-1)}`)
    else if (/発注受付/.test(notes)) summary.push('SGP は「発注受付」のまま')
    return { kind: 'A', reason: 'no_ship_record', summary, key: 'A', parsed }
  }

  if ((order.status === 'ordered' || order.status === 'shipped') && order.is_fully_received !== true) {
    if (!parsed.hasDelay) return null
    const summary = []
    if (parsed.changedTo.length) summary.push(`納期変更後:${parsed.changedTo.at(-1)}`)
    else summary.push('SGP から納期遅延の連絡あり')
    if (parsed.shipDates.length) summary.push(`SGP では ${md(parsed.shipDates.at(-1))} 発送`)
    return { kind: 'B', reason: 'delivery_changed', summary, key: delayKeyOf(parsed), parsed }
  }
  return null
}

// 確認済みの記録(sgp_review_confirmations の行)で消えるか。
//   A は一度確認すれば消える。B は「納期変更後」の文言が確認したときと同じ間だけ消える(新しい遅延はもう一度出す)。
export function isConfirmed(judgement, confirmations) {
  if (!judgement) return false
  return (confirmations ?? []).some(c => c && c.kind === judgement.kind && (judgement.kind === 'A' || c.notes_key === judgement.key))
}

// 候補の行から、まだ確認されていない要確認だけを返す(並び: A → B、各々 荷受け日/発注日の新しい順)。
export function pendingSgpReviews(orders, confirmationsByOrder = {}) {
  const out = []
  for (const o of orders ?? []) {
    const j = judgeSgpReview(o)
    if (!j) continue
    if (isConfirmed(j, confirmationsByOrder[o.order_id])) continue
    out.push({ order: o, judgement: j })
  }
  const sortKey = r => (r.judgement.kind === 'A' ? (r.order.arrived_at ?? '') : (r.order.order_date ?? ''))
  return out.sort((a, b) =>
    a.judgement.kind !== b.judgement.kind
      ? (a.judgement.kind === 'A' ? -1 : 1)
      : String(sortKey(b)).localeCompare(String(sortKey(a))))
}
